import {
    ADYEN_RESULT_CODE,
    HTTP_STATUS_NOT_FOUND,
    PAGE_TEXT,
    PAYMENT_LINK_STATE,
    PAYMENT_REFUSAL,
    STATUS_ACTION,
    STATUS_TONE,
} from "./paymentPage.constants";
import { PaymentPageRequestError } from "./paymentPage.api";

export const VIEW_KIND = Object.freeze({
    LOADING: "LOADING",
    PAYMENT_FORM: "PAYMENT_FORM",
    STATUS: "STATUS",
});

const RESULT_SCREEN = Object.freeze({ isTitleVisible: true, isSummaryVisible: true, isPollingPageState: false });
const WAITING_SCREEN = Object.freeze({ isTitleVisible: true, isSummaryVisible: true, isPollingPageState: true });
const ERROR_SCREEN = Object.freeze({ isTitleVisible: false, isSummaryVisible: false, isPollingPageState: false });

function buildStatusView(screen, tone, heading, body, action) {
    return Object.freeze({ kind: VIEW_KIND.STATUS, ...screen, tone, heading, body, action });
}

function buildUnableToLoadView(body, action) {
    return buildStatusView(ERROR_SCREEN, STATUS_TONE.WARNING, PAGE_TEXT.UNABLE_TO_LOAD, body, action);
}

export const PAGE_VIEW = Object.freeze({
    LOADING: Object.freeze({
        kind: VIEW_KIND.LOADING,
        isTitleVisible: true,
        isSummaryVisible: false,
        isPollingPageState: false,
    }),
    PAYMENT_FORM: Object.freeze({
        kind: VIEW_KIND.PAYMENT_FORM,
        isTitleVisible: true,
        isSummaryVisible: true,
        isPollingPageState: false,
    }),
    PAYMENT_SUCCESSFUL: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.SUCCESS,
        PAGE_TEXT.PAYMENT_SUCCESSFUL,
        PAGE_TEXT.PAYMENT_SUCCESSFUL_BODY,
        STATUS_ACTION.NONE
    ),
    PAYMENT_REFUSED: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.ERROR,
        PAGE_TEXT.PAYMENT_REFUSED,
        PAGE_TEXT.PAYMENT_REFUSED_BODY,
        STATUS_ACTION.TRY_AGAIN
    ),
    PAYMENT_REFUSED_FINAL: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.ERROR,
        PAGE_TEXT.PAYMENT_REFUSED,
        PAGE_TEXT.PAYMENT_REFUSED_FINAL_BODY,
        STATUS_ACTION.NONE
    ),
    PAYMENT_ERROR: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.ERROR,
        PAGE_TEXT.PAYMENT_ERROR,
        PAGE_TEXT.PAYMENT_ERROR_BODY,
        STATUS_ACTION.TRY_AGAIN
    ),
    PAYMENT_FAILED: buildUnableToLoadView(PAGE_TEXT.PAYMENT_FAILED_BODY, STATUS_ACTION.TRY_AGAIN),
    LOAD_FAILED: buildUnableToLoadView(PAGE_TEXT.LOAD_FAILED_BODY, STATUS_ACTION.NONE),
    LINK_NOT_FOUND: buildUnableToLoadView(PAGE_TEXT.LINK_NOT_FOUND_BODY, STATUS_ACTION.NONE),
    LINK_PAID: buildUnableToLoadView(PAGE_TEXT.LINK_PAID_BODY, STATUS_ACTION.NONE),
    LINK_EXPIRED: buildUnableToLoadView(PAGE_TEXT.LINK_EXPIRED_BODY, STATUS_ACTION.NONE),
    LINK_CLOSED: buildUnableToLoadView(PAGE_TEXT.LINK_CLOSED_BODY, STATUS_ACTION.NONE),
    PAYMENT_IN_PROGRESS: buildStatusView(
        WAITING_SCREEN,
        STATUS_TONE.PROGRESS,
        PAGE_TEXT.PAYMENT_IN_PROGRESS,
        PAGE_TEXT.PAYMENT_IN_PROGRESS_BODY,
        STATUS_ACTION.NONE
    ),
    PAYMENT_STARTED_ELSEWHERE: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.WARNING,
        PAGE_TEXT.PAYMENT_STARTED_ELSEWHERE,
        PAGE_TEXT.PAYMENT_STARTED_ELSEWHERE_BODY,
        STATUS_ACTION.RELOAD
    ),
    PAGE_TIMED_OUT: buildStatusView(
        RESULT_SCREEN,
        STATUS_TONE.WARNING,
        PAGE_TEXT.PAGE_TIMED_OUT,
        PAGE_TEXT.PAGE_TIMED_OUT_BODY,
        STATUS_ACTION.TRY_AGAIN
    ),
    CHECKING_PAYMENT: buildStatusView(
        WAITING_SCREEN,
        STATUS_TONE.PROGRESS,
        PAGE_TEXT.CHECKING_PAYMENT,
        PAGE_TEXT.CHECKING_PAYMENT_BODY,
        STATUS_ACTION.NONE
    ),
});

const VIEW_BY_LINK_STATE = Object.freeze({
    [PAYMENT_LINK_STATE.PAID]: PAGE_VIEW.LINK_PAID,
    [PAYMENT_LINK_STATE.EXPIRED]: PAGE_VIEW.LINK_EXPIRED,
    [PAYMENT_LINK_STATE.CLOSED]: PAGE_VIEW.LINK_CLOSED,
});

const VIEW_BY_REFUSAL = Object.freeze({
    [PAYMENT_REFUSAL.LINK_PAID]: PAGE_VIEW.LINK_PAID,
    [PAYMENT_REFUSAL.LINK_EXPIRED]: PAGE_VIEW.LINK_EXPIRED,
    [PAYMENT_REFUSAL.LINK_CLOSED]: PAGE_VIEW.LINK_CLOSED,
    [PAYMENT_REFUSAL.PAYMENT_IN_PROGRESS]: PAGE_VIEW.PAYMENT_IN_PROGRESS,
    [PAYMENT_REFUSAL.RELOAD_REQUIRED]: PAGE_VIEW.PAYMENT_STARTED_ELSEWHERE,
    [PAYMENT_REFUSAL.SESSION_EXPIRED]: PAGE_VIEW.PAGE_TIMED_OUT,
    [PAYMENT_REFUSAL.ATTEMPT_REPLACED]: PAGE_VIEW.PAYMENT_STARTED_ELSEWHERE,
});

function hasMappedView(viewTable, key) {
    return Object.prototype.hasOwnProperty.call(viewTable, key);
}

function isRefusal(error) {
    if (error instanceof PaymentPageRequestError) {
        return hasMappedView(VIEW_BY_REFUSAL, error.refusal);
    }
    return false;
}

function isNotFound(error) {
    if (error instanceof PaymentPageRequestError) {
        return error.httpStatus === HTTP_STATUS_NOT_FOUND;
    }
    return false;
}

export function resolveViewForLinkState(linkState) {
    return VIEW_BY_LINK_STATE[linkState];
}

export function resolveViewForLoadError(error) {
    if (isRefusal(error)) {
        return VIEW_BY_REFUSAL[error.refusal];
    }
    if (isNotFound(error)) {
        return PAGE_VIEW.LINK_NOT_FOUND;
    }
    return PAGE_VIEW.LOAD_FAILED;
}

export function resolveViewForPaymentError(error) {
    if (isRefusal(error)) {
        return VIEW_BY_REFUSAL[error.refusal];
    }
    return PAGE_VIEW.PAYMENT_FAILED;
}

export function resolveViewForPaymentResult(resultCode) {
    if (resultCode === ADYEN_RESULT_CODE.AUTHORISED) {
        return PAGE_VIEW.PAYMENT_SUCCESSFUL;
    }
    if (resultCode === ADYEN_RESULT_CODE.REFUSED) {
        return PAGE_VIEW.PAYMENT_REFUSED;
    }
    return PAGE_VIEW.PAYMENT_ERROR;
}

export function resolveViewForDeclinedLink(linkState) {
    if (linkState === PAYMENT_LINK_STATE.CLOSED) {
        return PAGE_VIEW.PAYMENT_REFUSED_FINAL;
    }
    return PAGE_VIEW.PAYMENT_REFUSED;
}
